import React, { useEffect, useState, useRef } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useDraggable } from '../hooks/useDraggable';

const JobLogsModal = ({ jobId, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [status, setStatus] = useState('initializing');
  const logsEndRef = useRef(null);

  // Position for PiP
  const { position, handlers: dragHandlers } = useDraggable(() => {
    if (typeof window !== 'undefined') {
      return {
        x: window.innerWidth - 680, // 640px width + 40px margin
        y: window.innerHeight - (window.innerHeight * 0.6) - 40 // 60vh height + 40px margin
      };
    }
    return { x: 20, y: 20 };
  });

  useEffect(() => {
    if (!jobId) return;

    // Listen to the Firestore document for the specific jobId
    const unsubscribe = onSnapshot(doc(db, 'job_logs', jobId), (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        if (data.logs) {
          setLogs(data.logs);
        }
        if (data.status) {
          setStatus(data.status);
        }
      }
    }, (error) => {
      console.error("Error listening to job logs:", error);
    });

    return () => unsubscribe();
  }, [jobId]);

  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs]);

  const isFinished = status === 'completed' || status === 'failed';

  return (
    <div
      className="fixed bg-gray-900 rounded-xl shadow-2xl flex flex-col z-[100] overflow-hidden border border-gray-700"
      style={{
        width: '640px',
        height: '60vh',
        left: position.x + 'px',
        top: position.y + 'px',
        margin: 0
      }}
    >
      {/* Header */}
      <div
        className="bg-gray-800 px-6 py-4 flex justify-between items-center border-b border-gray-700 cursor-move select-none"
        {...dragHandlers}
      >
        <div className="flex items-center gap-3 pointer-events-none">
          <h3 className="text-lg font-mono font-semibold text-gray-100">
            Cloud Run Execution Logs
          </h3>
          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium uppercase tracking-wider ${status === 'completed' ? 'bg-green-900/50 text-green-400 border border-green-700' :
            status === 'failed' ? 'bg-red-900/50 text-red-400 border border-red-700' :
              'bg-blue-900/50 text-blue-400 border border-blue-700 animate-pulse'
            }`}>
            {status}
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Terminal Body */}
      <div className="p-4 bg-black flex-1 overflow-y-auto font-mono text-sm leading-relaxed">
        {logs.length === 0 ? (
          <div className="text-gray-500 animate-pulse">Waiting for logs...</div>
        ) : (
          logs.map((rawLog, index) => {
            const log = typeof rawLog === 'string' ? rawLog : (rawLog?.stringValue || JSON.stringify(rawLog || ''));
            // Very basic color coding based on log content
            let textColor = 'text-gray-300';
            if (log.toLowerCase().includes('error') || log.toLowerCase().includes('fail')) {
              textColor = 'text-red-400';
            } else if (log.toLowerCase().includes('success') || log.toLowerCase().includes('completed')) {
              textColor = 'text-green-400';
            } else if (log.toLowerCase().includes('warning')) {
              textColor = 'text-yellow-400';
            }

            return (
              <div key={index} className={`break-words ${textColor} py-0.5`}>
                {log}
              </div>
            );
          })
        )}
        <div ref={logsEndRef} />
      </div>

      {/* Footer */}
      {isFinished && (
        <div className="bg-gray-800 px-6 py-4 flex justify-end border-t border-gray-700">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 focus:ring-offset-gray-900"
          >
            Close Window
          </button>
        </div>
      )}
    </div>
  );
};

export default JobLogsModal;
